'use client'

/**
 * PostHogProvider
 *
 * Client-side PostHog analytics. Initializes after hydration and wraps the app
 * with PostHog React provider for feature flags and usePostHog hooks.
 *
 * Requires NEXT_PUBLIC_POSTHOG_KEY and NEXT_PUBLIC_POSTHOG_HOST in env.
 */

import { ReactNode, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import posthog from 'posthog-js'
import type { CaptureResult } from 'posthog-js'
import { PostHogProvider as PHProvider } from '@posthog/react'
import {
  ProductAnalyticsEventNames,
  trackProductEvent,
  type ProductAnalyticsSurface
} from '@/lib/analytics/product-events'

const NOISY_EXCEPTION_VALUES = [
  'CustomEvent captured as exception with keys: isTrusted',
  "'TimeoutError' captured as exception with message: 'operation timed out'",
  'Object captured as exception with keys: [object has no keys]'
]
const NOISY_EXCEPTION_TYPES = [
  'CustomEvent',
  'Event',
  'ProgressEvent',
  'MessageEvent',
  // Safari Web Locks DOMException — auth-js navigatorLock fallback.
  // Root fix is always installing auth.lock; this drops residual noise.
  'UnavailableError'
]
const NOISY_EXCEPTION_PATTERNS = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
  'Script error.',
  'contentScriptData.init_ts',
  'captured as exception with keys: isTrusted'
]
let hasInitializedPostHog = false

function readPostHogValues(value: unknown) {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string')
  }

  return typeof value === 'string' ? [value] : []
}

function readExceptionListEntries(properties: Record<string, unknown> | undefined) {
  const list = properties?.$exception_list
  if (!Array.isArray(list)) return [] as Array<{ type?: string; value?: string }>

  return list.filter(
    (entry): entry is { type?: string; value?: string } =>
      Boolean(entry) && typeof entry === 'object'
  )
}

function collectExceptionSignals(properties: Record<string, unknown> | undefined) {
  const values = [
    ...readPostHogValues(properties?.$exception_values),
    ...readPostHogValues(properties?.$exception_types),
    ...readPostHogValues(properties?.$exception_type),
    ...readPostHogValues(properties?.$exception_message)
  ]

  for (const entry of readExceptionListEntries(properties)) {
    if (typeof entry.type === 'string') values.push(entry.type)
    if (typeof entry.value === 'string') values.push(entry.value)
  }

  return values
}

function isNoisyExceptionSignal(value: string) {
  if (NOISY_EXCEPTION_VALUES.includes(value)) return true
  if (NOISY_EXCEPTION_TYPES.includes(value)) return true
  return NOISY_EXCEPTION_PATTERNS.some((pattern) => value.includes(pattern))
}

export function shouldSendPostHogEvent(event: CaptureResult | null) {
  if (!event || event.event !== '$exception') {
    return event
  }

  const signals = collectExceptionSignals(
    event.properties as Record<string, unknown> | undefined
  )
  const shouldDrop = signals.some(isNoisyExceptionSignal)

  return shouldDrop ? null : event
}

function getSurfaceFromPath(pathname: string): ProductAnalyticsSurface {
  if (pathname.startsWith('/spaces')) return 'spaces'
  if (pathname.startsWith('/nexus')) return 'nexus'
  if (pathname.startsWith('/chat')) return 'chat'
  if (pathname.startsWith('/try')) return 'public_chat'
  if (pathname.startsWith('/embed')) return 'embed'
  if (pathname.startsWith('/settings')) return 'settings'
  if (pathname === '/' || pathname.startsWith('/?')) return 'homepage'
  if (pathname.includes('pricing') || pathname.includes('subscription')) {
    return 'pricing'
  }

  return 'unknown'
}

function ProductPageViewTracker({ disabled }: { disabled: boolean }) {
  const pathname = usePathname()
  const lastTrackedPathRef = useRef<string | null>(null)

  useEffect(() => {
    if (disabled || !pathname || lastTrackedPathRef.current === pathname) return

    lastTrackedPathRef.current = pathname
    const surface = getSurfaceFromPath(pathname)

    void trackProductEvent(ProductAnalyticsEventNames.PAGE_VIEWED, {
      surface,
      source: 'posthog_provider',
      trigger: 'route_change',
      page_path: pathname,
      page_title: typeof document === 'undefined' ? undefined : document.title
    })

    if (surface === 'pricing') {
      void trackProductEvent(ProductAnalyticsEventNames.PRICING_PAGE_VIEWED, {
        surface: 'pricing',
        source: 'posthog_provider',
        trigger: 'route_change',
        page_path: pathname
      })
    }
  }, [disabled, pathname])

  return null
}

export function PostHogProvider({
  children,
  disabled = false
}: {
  children: ReactNode
  disabled?: boolean
}) {
  useEffect(() => {
    if (disabled || hasInitializedPostHog) return

    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'
    if (typeof window === 'undefined' || !key) return

    posthog.init(key, {
      api_host: host,
      // Capture native $pageview events on SPA route changes. Web Analytics
      // (visitors, sessions, paths, bounce) is built on $pageview — without
      // this it stays empty even though custom events, errors, and logs flow.
      // 'history_change' is the correct SPA mode (no double-fire) with the
      // 2026-01-30 defaults; our custom PAGE_VIEWED event still fires alongside.
      capture_pageview: 'history_change',
      person_profiles: 'identified_only',
      defaults: '2026-01-30',
      before_send: shouldSendPostHogEvent,
      // Session recording is enabled for self-driving replay scouts. Mask
      // sensitive input by default so credentials/PII are never captured; the
      // live-browser / computer-interaction surfaces additionally carry
      // .ph-no-capture to mask their whole subtree.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask], .ph-no-capture'
      },
      loaded: (ph) => {
        if (process.env.NODE_ENV === 'development') ph.debug()
      }
    })
    hasInitializedPostHog = true
  }, [disabled])

  return (
    <PHProvider client={posthog}>
      <ProductPageViewTracker disabled={disabled} />
      {children}
    </PHProvider>
  )
}
